/**
 * FLIGHT ASSIGNMENTS — the flight roster, the formation thumbnail, and the four
 * selector fields under them.
 *
 * Split out of `missions-screens.ts` when G34 filled the formation well: that
 * file draws all four of FLY SINGLE MISSION's screens and this is the largest
 * of them, so it goes in its own module rather than pushing the other three
 * past the size gate. Geometry still comes from `flight-assignments.png`;
 * `missions-screens.ts` re-exports everything here that had a caller.
 */

import {
  aircraftLabel,
  defaultFormation,
  formationStation,
  formationsFor,
  playerFlightOf,
  type Formation,
  type MissionFlight,
  type MissionSpec,
} from "@red-opus/engine";
import type { Rect } from "../render/draw-ops.ts";
import { measureText, type FontAtlas } from "../render/text-metrics.ts";
import type { MenuAction } from "./menu-model.ts";
import { actionRow, type ActionSpec } from "./panel-parts.ts";
import type { ShellScreen, Widget } from "./shell-screen.ts";

const openCommand = (field: string) => ({ kind: "command" as const, command: `open:${field}` });

/** The light-grey plate the formation thumbnail is drawn on — measured at index 87 in the capture. */
export const THUMBNAIL_FILL = 87;

/** The thumbnail's well, measured from `flight-assignments.png`. */
export const FORMATION_WELL: Rect = { x: 185, y: 64, w: 107, h: 58 };

/** The blank margin inside the well, so no aeroplane sits on its frame. */
const THUMBNAIL_PAD = 10;
/** One aeroplane's plan-view symbol: a wing bar this wide, and the leader gets a tail under it. */
const WING_SPAN_PX = 7;

export interface FormationMark {
  /** Left end of the wing bar. */
  readonly x: number;
  readonly y: number;
  readonly leader: boolean;
}

/**
 * Where the formation thumbnail's aeroplanes go inside the well (G34/B5: the
 * well was drawn at (185, 64, 107, 58) with nothing in it, where the original
 * shows a live thumbnail that follows the selector).
 *
 * The shape is `mission/formations.ts`'s own station table read in the leader's
 * frame — heading 0, leader at the origin — so the picture and the aeroplanes
 * that actually take off are the same data. Only two of the three axes are
 * drawn: this is a plan view, and `y` (the stack) is what `line astern` and
 * `echelon` step down through, which a top-down diagram cannot show and the
 * original's could not either.
 *
 * The two axes share one scale, so a formation is never sheared to fill the
 * box — `line abreast` stays a wide flat row and `line astern` a narrow column,
 * which is the whole point of looking at it.
 */
export function formationMarks(formation: Formation, flightSize: number, well: Rect): readonly FormationMark[] {
  const count = Math.max(1, flightSize);
  const origin = { x: 0, y: 0, z: 0 };
  const stations = Array.from({ length: count }, (_, index) => formationStation(formation, index, origin, 0));
  const minX = Math.min(...stations.map((station) => station.x));
  const maxX = Math.max(...stations.map((station) => station.x));
  const minZ = Math.min(...stations.map((station) => station.z));
  const maxZ = Math.max(...stations.map((station) => station.z));
  // The wing bar has width of its own, so the room the *positions* may use is
  // the box less one span; forgetting that is what pushes the rightmost
  // aeroplane of a four-ship `line abreast` through the frame.
  const usableW = well.w - 2 * THUMBNAIL_PAD - WING_SPAN_PX;
  const usableH = well.h - 2 * THUMBNAIL_PAD;
  const spanX = maxX - minX;
  const spanZ = maxZ - minZ;
  const fits = [spanX > 0 ? usableW / spanX : Infinity, spanZ > 0 ? usableH / spanZ : Infinity];
  const scale = Math.min(...fits);
  const fit = Number.isFinite(scale) ? scale : 0;
  const left = well.x + THUMBNAIL_PAD + (usableW - spanX * fit) / 2;
  // `+z` is astern, and astern is down the page: the flight flies up the well.
  const top = well.y + THUMBNAIL_PAD + (usableH - spanZ * fit) / 2;
  return stations.map((station, index) => ({
    x: Math.round(left + (station.x - minX) * fit),
    y: Math.round(top + (station.z - minZ) * fit),
    leader: index === 0,
  }));
}

/**
 * The thumbnail as widgets: a wing bar per aeroplane, and a one-pixel tail under
 * the leader's so the flight has a nose to read it by. `underline` is the
 * primitive because it is already a two-tone horizontal bar in the shell's own
 * palette — a formation diagram wants nothing else.
 */
function formationThumbnail(formation: Formation, flightSize: number, well: Rect): Widget[] {
  return formationMarks(formation, flightSize, well).flatMap((mark): Widget[] => [
    { kind: "underline", x: mark.x, y: mark.y, w: WING_SPAN_PX },
    ...(mark.leader
      ? [{ kind: "underline" as const, x: mark.x + Math.floor(WING_SPAN_PX / 2), y: mark.y + 3, w: 1 }]
      : []),
  ]);
}

/**
 * The skill word for the `NAME/SKILL LEVEL` column, which printed the name and
 * dropped the half of its own header that says "skill level" (G34/B5).
 *
 * `novice`, `regular` and `veteran` are `PS.EXE`'s own words, lowercase, sitting
 * in the same string block as the formation names and `regular`/`incendiary`.
 * The binary has no fourth: the port's `ace` skill is what a *named* ace flies
 * at, and the game gives those pilots their names rather than a rating, so the
 * word here is the port's for a case the original's roster never prints.
 * The player's own row has no rating at all — he is the one flying.
 */
export function skillLabelOf(pilot: MissionFlight["pilot"]): string {
  return pilot.kind === "player" ? "" : pilot.skill;
}

/** The `NAME/SKILL LEVEL` column: from x=87 to the thumbnail well's left edge, less a gutter. */
const NAME_X = 87;
const NAME_COLUMN_RIGHT = FORMATION_WELL.x - 2;
/** Blank between the longest name and the rating beside it. */
const NAME_GUTTER = 5;

/**
 * One roster row's two texts: the callsign, and the rating right-aligned against
 * the thumbnail well.
 *
 * The rating is right-aligned rather than set at a fixed x because the column is
 * only ~96 px wide and `Balloon guard (2)` is 17 characters of a 7-px font —
 * it already ran past the well's edge before this lane added anything. So the
 * name is cut to whatever the rating leaves, which is the same column discipline
 * `paint-screens.ts` applies to its caption strip. No ellipsis: `FONT3` is the
 * original's atlas and has no character for one.
 */
function rosterRow(flight: MissionFlight, y: number, font: FontAtlas): Widget[] {
  const skill = skillLabelOf(flight.pilot);
  const skillWidth = skill === "" ? 0 : measureText(font, skill);
  const nameRoom = NAME_COLUMN_RIGHT - NAME_X - (skill === "" ? 0 : skillWidth + NAME_GUTTER);
  let name = flight.callsign;
  while (name.length > 1 && measureText(font, name) > nameRoom) name = name.slice(0, -1);
  return [
    { kind: "text", text: name, x: NAME_X, y, role: "label" },
    ...(skill === ""
      ? []
      : [{ kind: "text" as const, text: skill, x: NAME_COLUMN_RIGHT - skillWidth, y, role: "label" as const }]),
  ];
}

/** `flight-assignments.png`: the roster plate, its table, and the four selector fields. */
export const ROSTER_PLATE: Rect = { x: 20, y: 41, w: 279, h: 127 };
const ROSTER_ROW_Y = 74;
const ROSTER_ROW_PITCH = 7;

interface FieldSpec {
  readonly id: string;
  readonly label: readonly string[];
  readonly labelX: readonly number[];
  readonly labelY: number;
  readonly rect: Rect;
  readonly value: string;
  /**
   * `undefined` keeps the field a decorative readout. Nothing on this screen is
   * one any more: `FORMATION` and `TOTAL AIRCRAFT IN FLIGHT` were the two that
   * stayed decorative through AD-011, on the grounds that wiring them "means
   * growing or shrinking the flight itself" — which is exactly what Dogfight a
   * Squadron needed, so both are pickers now.
   */
  readonly action?: MenuAction;
}

function fieldWidgets(fields: readonly FieldSpec[]): Widget[] {
  return fields.flatMap((field) => [
    ...field.label.map((line, i): Widget => ({
      kind: "text",
      text: line,
      x: field.labelX[i] ?? field.labelX[0] ?? 0,
      y: field.labelY + i * ROSTER_ROW_PITCH,
      role: "label",
    })),
    { kind: "value", id: field.id, rect: field.rect, text: field.value, enabled: field.action !== undefined, action: field.action },
  ]);
}

/**
 * The formation word this flight is actually flying.
 *
 * It used to be the port's own guess — `solo` for one aeroplane and a flat
 * `line astern` for everything else — because nothing placed aeroplanes by
 * formation. Now `mission/formations.ts` does, so the readout is recovered from
 * the spec instead of asserted: the wingmen's stations are matched against each
 * legal formation's own geometry, and the one they are standing in is the one
 * named. A flight whose generator never used a formation still reads
 * `defaultFormation`, which is what it was placed with.
 *
 * **Two formations can be the same shape, and the first match wins.** `vee wing`
 * and `diamond` differ only in the *fourth* aeroplane — whether the tail-end
 * one sits below the V or above it — so a flight of three flying either is
 * standing in identical positions and this reports `vee wing`, the earlier of
 * the two in `PS.EXE`'s own order. That is a fact about the shapes rather than a
 * limitation of the search: there is nothing in a three-ship spec that could
 * distinguish them, and inventing a distinction would mean storing the chosen
 * word on the spec, which no `MissionFlight` field carries.
 */
export function formationOf(spec: MissionSpec): Formation {
  const friendly = spec.flights.filter((flight) => flight.side === "friendly");
  const leader = friendly[0];
  if (!leader || friendly.length <= 1) return "solo";
  const match = formationsFor(friendly.length).find((candidate) =>
    friendly.every((flight, index) => {
      const station = formationStation(candidate, index, leader.position, leader.headingRad);
      // Height is part of the match, not a detail of it: `vee wing` and
      // `diamond` put their aeroplanes at identical ground positions and differ
      // only in whether the tail-end one sits above or below the V.
      return (
        Math.hypot(
          station.x - flight.position.x,
          station.y - flight.position.y,
          station.z - flight.position.z,
        ) < 1
      );
    }),
  );
  return match ?? defaultFormation(friendly.length);
}

/**
 * `font` is FONT3 — the atlas `rosterRow` trims names against and the renderer
 * draws `role: "label"` in. `heading` is FONT1, which is what `boardButtonOps`
 * draws the action row's labels in. Both call sites used to pass FONT1 for both,
 * so every roster name was measured 15% wide and cut shorter than it needed to
 * be, and the right-aligned rating sat left of where it is drawn.
 */
export function flightAssignmentsScreen(
  spec: MissionSpec,
  font: FontAtlas,
  heading: FontAtlas,
  actions: readonly ActionSpec[],
): ShellScreen {
  const player = playerFlightOf(spec);
  const friendly = spec.flights.filter((flight) => flight.side === "friendly");
  const rows = friendly.map((_flight, index): Widget => ({
    kind: "text",
    text: index === 0 ? "LEADER" : String(index + 1),
    x: 36,
    y: ROSTER_ROW_Y + index * ROSTER_ROW_PITCH,
    role: "label",
  }));
  const names = friendly.flatMap((flight, index) =>
    rosterRow(flight, ROSTER_ROW_Y + index * ROSTER_ROW_PITCH, font),
  );
  const formation = formationOf(spec);
  const fields: FieldSpec[] = [
    {
      id: "formation",
      label: ["FORMATION"],
      labelX: [40],
      labelY: 132,
      rect: { x: 90, y: 129, w: 62, h: 13 },
      value: formation,
      action: openCommand("formation"),
    },
    {
      id: "aircraft",
      label: ["YOUR", "AIRCRAFT"],
      labelX: [176, 159],
      labelY: 128,
      rect: { x: 202, y: 129, w: 90, h: 13 },
      value: aircraftLabel(player.aircraft),
      action: openCommand("aircraft"),
    },
    {
      id: "total",
      label: ["TOTAL AIRCRAFT", "IN FLIGHT"],
      labelX: [26, 48],
      labelY: 145,
      rect: { x: 100, y: 146, w: 52, h: 13 },
      value: String(friendly.length),
      action: openCommand("total"),
    },
    {
      id: "ammunition",
      label: ["AMMUNITION"],
      labelX: [171],
      labelY: 149,
      rect: { x: 226, y: 146, w: 66, h: 13 },
      value: player.ammunition ?? "regular",
      action: openCommand("ammo"),
    },
  ];
  return {
    id: "missions/roster",
    chrome: { kind: "panel", title: "FLIGHT ASSIGNMENTS", emblem: "roundel", background: undefined },
    widgets: [
      { kind: "text", text: "You are the flight leader.", x: 102, y: 26, role: "prose" },
      { kind: "plate", rect: ROSTER_PLATE },
      { kind: "text", text: "FLIGHT ROSTER", x: 126, y: 44, role: "heading" },
      { kind: "rules", x: 85, y: 47, length: 28, toward: -1 },
      { kind: "rules", x: 209, y: 47, length: 28, toward: 1 },
      { kind: "text", text: "POSITION", x: 36, y: 63, role: "label" },
      { kind: "text", text: "NAME/SKILL LEVEL", x: 87, y: 63, role: "label" },
      ...rows,
      ...names,
      { kind: "well", rect: FORMATION_WELL, face: THUMBNAIL_FILL },
      ...formationThumbnail(formation, friendly.length, FORMATION_WELL),
      ...fieldWidgets(fields),
      ...actionRow(actions, heading, 179),
    ],
    cancel: actions[0]?.action,
  };
}

